import React, { useEffect } from 'react';
import { BookOpen, CheckCircle2, X, PenTool, StickyNote, Download, ArrowRight, ArrowLeft } from 'lucide-react';
import { translations } from '../lib/i18n';
import { useData } from '../context/DataContext';
import type { Lesson } from '../lib/types';

interface LessonModeModalProps {
  lesson: Lesson;
  questionCount: number;
  wilaya: string;
  onSelectTheory: () => void;
  onSelectQuiz: () => void;
  onClose: () => void;
}

export const LessonModeModal: React.FC<LessonModeModalProps> = ({
  lesson,
  questionCount,
  wilaya,
  onSelectTheory,
  onSelectQuiz,
  onClose,
}) => {
  const { language } = useData();
  const t = translations[language];
  const isRtl = language === 'ar';

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="lesson-mode-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lesson-mode-title"
    >
      <div
        className="lesson-mode-modal"
        onClick={(e) => e.stopPropagation()}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        {/* Close Button */}
        <button
          type="button"
          className="lesson-mode-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Modal Header */}
        <div className="lesson-mode-header">
          <span className="lesson-mode-badge">
            <BookOpen size={13} />
            <span>{t.theoryTitle}</span>
          </span>
          <h2 id="lesson-mode-title" className="lesson-mode-title" dir="auto">
            {lesson.name}
          </h2>
          <p className="lesson-mode-desc">{t.chooseLessonModeDesc}</p>
        </div>

        {/* Mode Options Cards */}
        <div className="lesson-mode-cards-grid">
          {/* OPTION 1: THEORY STUDIO */}
          <div
            className="lesson-mode-card card-theory"
            onClick={onSelectTheory}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectTheory();
              }
            }}
          >
            <div className="mode-card-header">
              <div className="mode-icon-circle theory-circle">
                <BookOpen size={24} />
              </div>
              <div>
                <h3 className="mode-card-title">{t.openTheoryStudio}</h3>
                <span className="mode-card-badge-soft">
                  <PenTool size={11} /> {t.drawPen} & {t.savePdf}
                </span>
              </div>
            </div>

            <p className="mode-card-description">{t.openTheoryStudioDesc}</p>

            <ul className="mode-features-list">
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'تلوين وتحديد الفقرات والنقاط السريرية' : 'Surlignage et repères visuels'}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'رسم حر وتوضيحات بالقلم التفاعلي' : 'Dessin libre et annotations manuscrites'}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'إضافة ملاحظات لاصقة وقابلة للتعديل' : 'Notes autocollantes interactives'}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'تصدير كامل الدرس مع رسوماتك كـ PDF' : 'Téléchargement et impression PDF'}</span>
              </li>
            </ul>

            <button
              type="button"
              className="btn-primary mode-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onSelectTheory();
              }}
            >
              <span>{language === 'ar' ? 'عرض الدرس النظري' : language === 'fr' ? 'Ouvrir le cours' : 'Open Lecture'}</span>
              {isRtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
            </button>
          </div>

          {/* OPTION 2: MCQ PRACTICE BANK */}
          <div
            className="lesson-mode-card card-quiz"
            onClick={onSelectQuiz}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectQuiz();
              }
            }}
          >
            <div className="mode-card-header">
              <div className="mode-icon-circle quiz-circle">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h3 className="mode-card-title">{t.openQuizMode}</h3>
                <span className="mode-card-badge-soft">
                  {questionCount} {questionCount === 1 ? t.questionSingular : t.questionsCount}
                </span>
              </div>
            </div>

            <p className="mode-card-description">{t.openQuizModeDesc}</p>

            <ul className="mode-features-list">
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? `أسئلة دورات حقيقية (${wilaya === 'all' ? t.allWilayas : wilaya})` : `Examens officiels (${wilaya === 'all' ? t.allWilayas : wilaya})`}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'تصحيح تلقائي فوري مع شروحات الأسئلة' : 'Correction instantanée détaillée'}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'شاشة مدمجة بدون تمرير مريح للعين' : 'Affichage compact sans défilement'}</span>
              </li>
              <li>
                <span className="feature-bullet" />
                <span>{language === 'ar' ? 'حفظ التقدم ومراجعة الأخطاء فقط' : 'Suivi de score et révision des erreurs'}</span>
              </li>
            </ul>

            <button
              type="button"
              className="btn-secondary mode-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onSelectQuiz();
              }}
            >
              <span>{t.startPracticeFromLesson}</span>
              {isRtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
